import { Check, Loader2 } from 'lucide-react'
import { useT } from '../../i18n/index.js'

// ── Types ──

export type StepName = 'transcribing' | 'extracting' | 'executed'
export type StepStatus = 'done' | 'active' | 'pending'

export interface ProcessingStep {
  name: StepName
  status: StepStatus
  detail?: string
}

interface ProcessingStepsProps {
  steps: ProcessingStep[]
}

// ── Component ──

export function ProcessingSteps({ steps }: ProcessingStepsProps) {
  const t = useT()

  const STEP_LABELS: Record<StepName, string> = {
    transcribing: t.voice.stepTranscription,
    extracting: t.voice.stepExtraction,
    executed: t.voice.stepExecution,
  }

  return (
    <div className="flex flex-col gap-0" role="list" aria-label={t.voice.processingSteps}>
      {steps.map((step) => (
        <div
          key={step.name}
          className="flex items-center gap-3 py-2"
          role="listitem"
          aria-current={step.status === 'active' ? 'step' : undefined}
        >
          <StepIcon status={step.status} />
          <div className="min-w-0 flex-1">
            <p
              className="text-sm font-medium"
              style={{ color: step.status === 'pending' ? 'var(--color-text-disabled)' : 'var(--color-text)' }}
            >
              {STEP_LABELS[step.name]}
            </p>
            {step.detail && (
              <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                {step.detail}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Step icon ──

function StepIcon({ status }: { status: StepStatus }) {
  const baseClasses = 'flex h-5 w-5 shrink-0 items-center justify-center rounded-full'

  if (status === 'done') {
    return (
      <div
        className={baseClasses}
        style={{ background: 'var(--color-status-success-light)', color: 'var(--color-status-success)' }}
      >
        <Check size={12} aria-hidden="true" />
      </div>
    )
  }

  if (status === 'active') {
    return (
      <div
        className={baseClasses}
        style={{ background: 'var(--color-status-info-light)', color: 'var(--color-status-info)' }}
      >
        <Loader2 size={12} className="animate-spin" aria-hidden="true" />
      </div>
    )
  }

  // pending
  return (
    <div
      className={baseClasses}
      style={{ background: 'var(--color-surface-raised)', color: 'var(--color-text-disabled)' }}
    >
      <div className="h-1.5 w-1.5 rounded-full" style={{ background: 'currentColor' }} />
    </div>
  )
}
